// Employee Self-Service (ESS) Module
// Mobile-first design for employee use on handheld devices

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getEmployees,
  getCustomers,
  getFacilities,
  getServices,
  getSupplierPricing,
  getWorkOrders,
  createWorkOrder,
  getInternalOrders,
  createInternalOrder,
  getLeaveRequests,
  createLeaveRequest,
  deleteLeaveRequest,
  getDispatchLog,
  getWorkDocuments,
  createWorkDocument,
  getMyPayslips,
  getAssignedTasks,
  updateAssignedTask
} from '../services/api';import { getCurrency, getCurrencySymbol } from '../lib/currency';
import {
  Clock,
  Calendar,
  FileText,
  Send,
  Camera,
  Plus,
  X,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  XCircle,
  Upload,
  Image,
  Trash2,
  Info,
  Briefcase,
  Home as HomeIcon,
  Users,
  Search,
  Phone,
  Mail,
  MapPin,
  LogOut,
  ClipboardCheck,
  PlayCircle
} from 'lucide-react';
const Home = () => {
  const { user, employee: authEmployee, logout } = useAuth();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('home');
  
  // Customer/facility browse state
  const [customerSearch, setCustomerSearch] = useState('');
  const [expandedCustomerId, setExpandedCustomerId] = useState(null);
  
  // Shared data
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [services, setServices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  // Owner/admin override: explicitly act as a chosen employee record.
  // Persisted to localStorage so the choice survives reloads.
  const [viewAsEmployeeId, setViewAsEmployeeId] = useState(() => {
    try { return localStorage.getItem('ess_view_as_employee_id') || ''; } catch (_) { return ''; }
  });
  // Manager-view: show ALL records in the org (owners/admins only).
  const [viewAllMode, setViewAllMode] = useState(() => {
    try { return localStorage.getItem('ess_view_all_mode') === '1'; } catch (_) { return false; }
  });
  // Selected month filter (YYYY-MM). Defaults to the current month.
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
  });
  // When true, only entries in the selected month are shown in lists.
  // Defaults to true so old/stale pending entries from prior months stay hidden.
  const [filterListsByMonth, setFilterListsByMonth] = useState(true);
  const [loading, setLoading] = useState(true);
  const [currencySymbol, setCurrencySymbol] = useState('kr');
  
  // Work order (time entry) state
  const [myWorkOrders, setMyWorkOrders] = useState([]);
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [servicePricing, setServicePricing] = useState(null);
  const [serviceSearch, setServiceSearch] = useState('');
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [workOrderForm, setWorkOrderForm] = useState({
    customerType: 'existing',
    anleggsnr: '',
    customer_id: '',
    customName: '',
    date: new Date().toISOString().split('T')[0],
    service_id: '',
    service_name: '',
    price_type: 'pris',
    supplier_id: '',
    description: '',
    arbeidstid: 0,
    kjoretid: 0,
    kjorte_km: 0
  });
  
  // Leave request state
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveForm, setLeaveForm] = useState({
    type: 'vacation', // vacation, sick, personal, other
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    reason: '',
    notes: ''
  });
  
  // My work orders (received dispatches)
  const [receivedDispatches, setReceivedDispatches] = useState([]);

  // Assigned tasks (work orders dispatched to me, with status lifecycle)
  const [assignedTasks, setAssignedTasks] = useState([]);
  const [taskBusyId, setTaskBusyId] = useState(null);

  // Pagination state for the three submission lists
  const PAGE_SIZE = 20;
  const [workOrderPage, setWorkOrderPage] = useState(1);
  const [internalPage, setInternalPage] = useState(1);
  const [leavePage, setLeavePage] = useState(1);
  
  // Work documentation
  const [workDocuments, setWorkDocuments] = useState([]);
  const [showDocModal, setShowDocModal] = useState(false);
  const [docForm, setDocForm] = useState({
    title: '',
    description: '',
    workOrderId: '',
    imageData: null
  });
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  // Payslips
  const [payslips, setPayslips] = useState([]);
  const [expandedPayslipId, setExpandedPayslipId] = useState(null);
  
  // Internal work state
  const [myInternalOrders, setMyInternalOrders] = useState([]);
  const [showInternalModal, setShowInternalModal] = useState(false);
  const [internalSubmitting, setInternalSubmitting] = useState(false);
  const [internalForm, setInternalForm] = useState({
    avdeling: '',
    date: new Date().toISOString().split('T')[0],
    beskrivelse: '',
    arbeidstid: 0,
    task_type: 'kontor',
    kommentar: ''
  });

  useEffect(() => {
    loadData();
    setCurrencySymbol(getCurrencySymbol(getCurrency()));
  }, []);

  // Seed currentEmployee from the auth context immediately so data loads
  // even before the employees list arrives (or if the email match fails).
  useEffect(() => {
    if (authEmployee && !currentEmployee) {
      setCurrentEmployee(authEmployee);
    }
    // We intentionally only react to authEmployee here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authEmployee]);

  useEffect(() => {
    // Try to upgrade to the full employee record from the loaded list using
    // tolerant matching across common email/identifier fields.
    if (employees.length === 0) return;

    // 1) Owner/admin override always wins.
    if (viewAsEmployeeId) {
      const forced = employees.find(e => String(e.id) === String(viewAsEmployeeId));
      if (forced) {
        setCurrentEmployee(forced);
        return;
      }
    }

    const emailLc = (user?.email || authEmployee?.epost || authEmployee?.email || '').toLowerCase();
    if (!emailLc) return;
    const match = employees.find(e => {
      const candidates = [e.epost, e.email, e.e_post, e.userEmail]
        .filter(Boolean)
        .map(v => String(v).toLowerCase());
      return candidates.includes(emailLc);
    });
    if (match) {
      setCurrentEmployee(match);
    } else if (authEmployee && !currentEmployee) {
      // Keep the auth-provided fallback (may be virtual)
      setCurrentEmployee(authEmployee);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, user, authEmployee, viewAsEmployeeId]);

  // Persist the "view as" override and reload data when it changes.
  useEffect(() => {
    try {
      if (viewAsEmployeeId) localStorage.setItem('ess_view_as_employee_id', viewAsEmployeeId);
      else localStorage.removeItem('ess_view_as_employee_id');
    } catch (_) { /* ignore */ }
  }, [viewAsEmployeeId]);

  // Persist the manager-view toggle and reload data when it changes.
  useEffect(() => {
    try {
      if (viewAllMode) localStorage.setItem('ess_view_all_mode', '1');
      else localStorage.removeItem('ess_view_all_mode');
    } catch (_) { /* ignore */ }
    if (currentEmployee) {
      loadEmployeeData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewAllMode]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Phase 1: Load only essential data for the UI
      const [employeesRes, customersRes, facilitiesRes, servicesRes, suppliersRes] = await Promise.all([
        getEmployees(),
        getCustomers(),
        getFacilities().catch(() => ({ data: [] })),
        getServices(),
        getSupplierPricing()
      ]);
      
      setEmployees(employeesRes.data || []);
      setCustomers(customersRes.data || []);
      setFacilities(facilitiesRes.data || []);
      setServices(servicesRes.data || []);
      setSuppliers(suppliersRes.data || []);
      setLoading(false);
      
      // Phase 2: Load secondary data in background
      const [dispatchRes, docsRes] = await Promise.all([
        getDispatchLog(),
        getWorkDocuments()
      ]);
      
      setWorkDocuments(docsRes.data || []);
      
      // Filter dispatches for current user
      const dispatches = dispatchRes.data || [];
      if (user?.email) {
        const myDispatches = dispatches.filter(d => 
          d.recipientEmail?.toLowerCase() === user.email.toLowerCase()
        );
        setReceivedDispatches(myDispatches);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      setLoading(false);
    }
  };

  // Load time entries and leave requests when employee is identified
  useEffect(() => {
    if (currentEmployee) {
      loadEmployeeData();
    }
  }, [currentEmployee]);

  const loadEmployeeData = async () => {
    if (!currentEmployee) return;
    
    try {
      const [workOrdersRes, leaveRes, payslipRes, internalRes] = await Promise.all([
        getWorkOrders(),
        getLeaveRequests(),
        getMyPayslips(currentEmployee.id),
        getInternalOrders()
      ]);
      // Match a record to "me" by employee_id OR by submitter email,
      // so records still show up if the employee_id was stored using a
      // different id (e.g. user id vs employee id).
      const myEmpId = String(currentEmployee.id || '');
      const myEmail = (user?.email || currentEmployee.epost || currentEmployee.email || '').toLowerCase();
      const isMine = (rec) => {
        if (!rec) return false;
        // Admin view: include everything
        if (viewAllMode) return true;
        if (myEmpId && String(rec.employee_id || '') === myEmpId) return true;
        const submitter = String(rec.submittedBy || rec.userEmail || rec.email || '').toLowerCase();
        if (myEmail && submitter && submitter === myEmail) return true;
        return false;
      };

      const allOrders = workOrdersRes.data || [];
      setMyWorkOrders(allOrders.filter(isMine));

      const allLeave = leaveRes.data || [];
      setLeaveRequests(allLeave.filter(isMine));

      const allInternal = internalRes.data || [];
      setMyInternalOrders(allInternal.filter(isMine));
      
      setPayslips(payslipRes.data || []);

      // Assigned tasks (work orders dispatched to me)
      try {
        const tasksRes = await getAssignedTasks();
        const allTasks = tasksRes.data || [];
        const isMyTask = (t) => {
          if (!t) return false;
          if (viewAllMode) return true;
          if (myEmpId && String(t.employeeId || '') === myEmpId) return true;
          const te = String(t.employeeEmail || '').toLowerCase();
          if (myEmail && te && te === myEmail) return true;
          return false;
        };
        setAssignedTasks(allTasks.filter(isMyTask));
      } catch (e) {
        console.warn('Failed to load assigned tasks:', e?.message);
      }
    } catch (error) {
      console.error('Failed to load employee data:', error);
    }
  };
  // Customer lookup
  const getCurrentCustomer = () => {
    if (workOrderForm.anleggsnr) {
      return customers.find(c => String(c.anleggsnr) === String(workOrderForm.anleggsnr));
    }
    return null;
  };

  const handleAnleggsnrChange = (anleggsnr) => {
    setWorkOrderForm({ ...workOrderForm, anleggsnr });
    const customer = customers.find(c => String(c.anleggsnr) === String(anleggsnr));
    if (customer) {
      setWorkOrderForm(f => ({ ...f, customer_id: customer.id }));
      // Auto-select service if customer has typenr
      if (customer.typenr) {
        const service = services.find(s => String(s.tjenestenr) === String(customer.typenr));
        if (service) {
          setServicePricing(service);
          setWorkOrderForm(f => ({
            ...f,
            service_id: service.id,
            service_name: service.tjeneste_navn,
            price_type: 'pris'
          }));
          setServiceSearch(service.tjeneste_navn);
        }
      }
    } else {
      setWorkOrderForm(f => ({ ...f, customer_id: '' }));
    }
  };

  // Service selection
  const handleServiceSelect = (service) => {
    setWorkOrderForm(f => ({
      ...f,
      service_id: service.id,
      service_name: service.tjeneste_navn,
      price_type: 'pris'
    }));
    setServicePricing(service);
    setServiceSearch(service.tjeneste_navn);
    setShowServiceDropdown(false);
  };

  const getFilteredServices = () => {
    if (!serviceSearch) return services.slice(0, 10);
    const search = serviceSearch.toLowerCase();
    return services.filter(s => 
      s.tjeneste_navn?.toLowerCase().includes(search) ||
      String(s.tjenestenr).includes(search)
    ).slice(0, 10);
  };

  const getPriceOptions = () => {
    if (!servicePricing) return [];
    const options = [];
    // Hide prices from employees - just show type labels
    if (servicePricing.pris) options.push({ value: 'pris', label: 'Fixed price' });
    if (servicePricing.t1_ekstraservice) options.push({ value: 't1_ekstraservice', label: 'Extra service (hourly)' });
    if (servicePricing.t2_kveldsarbeid) options.push({ value: 't2_kveldsarbeid', label: 'Evening work (hourly)' });
    if (servicePricing.t3_helgdag) options.push({ value: 't3_helgdag', label: 'Weekend/Holiday (hourly)' });
    return options;
  };

  const resetWorkOrderForm = () => {
    setWorkOrderForm({
      customerType: 'existing',
      anleggsnr: '',
      customer_id: '',
      customName: '',
      date: new Date().toISOString().split('T')[0],
      service_id: '',
      service_name: '',
      price_type: 'pris',
      supplier_id: '',
      description: '',
      arbeidstid: 0,
      kjoretid: 0,
      kjorte_km: 0
    });
    setServicePricing(null);
    setServiceSearch('');
  };

  const getCustomerName = (order) => {
    if (order.customName) return order.customName;
    const customer = customers.find(c => String(c.id) === String(order.customer_id) || String(c.anleggsnr) === String(order.anleggsnr));
    return customer?.kundnavn || `Facility #${order.anleggsnr}`;
  };

  const handleWorkOrderSubmit = async (e) => {
    e.preventDefault();
    if (!currentEmployee) {
      alert('Could not identify your employee profile. Please contact admin.');
      return;
    }
    
    setSubmitting(true);
    try {
      // Submit as work order with pending status
      await createWorkOrder({
        ...workOrderForm,
        employee_id: currentEmployee.id,
        status: 'pending', // For manager approval
        submittedBy: user?.email || currentEmployee.navn,
        submittedAt: new Date().toISOString(),
        arbeidstid: parseFloat(workOrderForm.arbeidstid) || 0,
        kjoretid: parseFloat(workOrderForm.kjoretid) || 0,
        kjorte_km: parseFloat(workOrderForm.kjorte_km) || 0
      });
      
      setShowTimeModal(false);
      resetWorkOrderForm();
      loadEmployeeData();
      alert('Work order submitted for approval!');
    } catch (error) {
      console.error('Failed to submit work order:', error);
      alert('Failed to submit: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Internal order functions
  const resetInternalForm = () => {
    setInternalForm({
      avdeling: '',
      date: new Date().toISOString().split('T')[0],
      beskrivelse: '',
      arbeidstid: 0,
      task_type: 'kontor',
      kommentar: ''
    });
  };

  const handleInternalSubmit = async (e) => {
    e.preventDefault();
    if (!currentEmployee) {
      alert('Could not identify your employee profile. Please contact admin.');
      return;
    }
    
    setInternalSubmitting(true);
    try {
      await createInternalOrder({
        ...internalForm,
        employee_id: currentEmployee.id,
        status: 'pending', // For manager approval
        submittedBy: user?.email || currentEmployee.navn,
        submittedAt: new Date().toISOString(),
        arbeidstid: parseFloat(internalForm.arbeidstid) || 0
      });
      
      setShowInternalModal(false);
      resetInternalForm();
      loadEmployeeData();
      alert('Internal work submitted for approval!');
    } catch (error) {
      console.error('Failed to submit internal work:', error);
      alert('Failed to submit: ' + error.message);
    } finally {
      setInternalSubmitting(false);
    }
  };

  const getTaskTypeLabel = (type) => {
    const labels = {
      kontor: 'Office Work',
      montering: 'Installation',
      vedlikehold: 'Maintenance',
      reise: 'Travel',
      diverse: 'Miscellaneous'
    };
    return labels[type] || type;
  };

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    if (!currentEmployee) {
      alert('Could not identify your employee profile. Please contact admin.');
      return;
    }
    
    try {
      await createLeaveRequest({
        ...leaveForm,
        employee_id: currentEmployee.id,
        employeeName: currentEmployee.navn
      });
      setShowLeaveModal(false);
      setLeaveForm({
        type: 'vacation',
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        reason: '',
        notes: ''
      });
      loadEmployeeData();
      alert('Leave request submitted for approval!');
    } catch (error) {
      console.error('Failed to submit leave request:', error);
      alert('Failed to submit leave request: ' + error.message);
    }
  };

  const handleDeleteLeave = async (id) => {
    if (!window.confirm('Delete this leave request?')) return;
    try {
      await deleteLeaveRequest(id);
      loadEmployeeData();
    } catch (error) {
      console.error('Failed to delete leave request:', error);
    }
  };

  const handleImageCapture = (e, source) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setDocForm(prev => ({
          ...prev,
          imageData: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDocSubmit = async (e) => {
    e.preventDefault();
    if (!currentEmployee) {
      alert('Could not identify your employee profile.');
      return;
    }
    
    try {
      await createWorkDocument({
        ...docForm,
        employee_id: currentEmployee.id,
        employeeName: currentEmployee.navn
      });
      setShowDocModal(false);
      setDocForm({
        title: '',
        description: '',
        workOrderId: '',
        imageData: null
      });
      loadData();
      alert('Documentation uploaded successfully!');
    } catch (error) {
      console.error('Failed to upload documentation:', error);
      alert('Failed to upload: ' + error.message);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: AlertCircle },
      approved: { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircle },
      rejected: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle }
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${style.bg} ${style.text}`}>
        <Icon size={12} />
        {status?.charAt(0).toUpperCase() + status?.slice(1) || 'Pending'}
      </span>
    );
  };

  const getLeaveTypeLabel = (type) => {
    const labels = {
      vacation: 'Vacation',
      sick: 'Sick Leave',
      personal: 'Personal Leave',
      other: 'Other'
    };
    return labels[type] || type;
  };

  const tabs = [
    { id: 'home', label: 'Home', icon: HomeIcon },
    { id: 'assigned', label: 'Assigned', icon: ClipboardCheck },
    { id: 'logtime', label: 'Log Work', icon: Clock },
    { id: 'internal', label: 'Internal', icon: Briefcase },
    { id: 'leave', label: 'Leave', icon: Calendar },
    { id: 'workorders', label: 'Dispatches', icon: Send },
    { id: 'customers', label: 'Customers', icon: Users },
    { id: 'docs', label: 'Documents', icon: Camera },
    { id: 'payslips', label: 'Payslips', icon: FileText }
  ];

  // === My Portal (home) — derived stats ===
  const pendingDispatchCount = receivedDispatches.filter(d => (d.status || 'pending') === 'pending').length;
  const pendingLeaveCount = leaveRequests.filter(l => (l.status || 'pending') === 'pending').length;
  const pendingTimeCount = myWorkOrders.filter(o => (o.status || 'pending') === 'pending').length;
  const openAssignedCount = assignedTasks.filter(t => t.status !== 'completed' && t.status !== 'rejected').length;
  const latestPayslip = payslips[0];

  // === Assigned task helpers ===
  const isTaskOverdue = (task) => {
    if (!task || !task.dueDate) return false;
    if (task.status === 'completed' || task.status === 'rejected') return false;
    const due = new Date(task.dueDate);
    if (isNaN(due.getTime())) return false;
    due.setHours(23, 59, 59, 999);
    return due.getTime() < Date.now();
  };

  const updateTaskStatus = async (task, status) => {
    setTaskBusyId(task.id);
    // optimistic update
    setAssignedTasks(prev => prev.map(t => t.id === task.id ? { ...t, status } : t));
    try {
      await updateAssignedTask(task.id, { status });
    } catch (e) {
      alert('Could not update task: ' + (e?.response?.data?.error || e.message || e));
      loadEmployeeData();
    } finally {
      setTaskBusyId(null);
    }
  };

  const TASK_STATUS_META = {
    assigned: { label: 'New', cls: 'bg-blue-500/15 text-blue-400' },
    in_progress: { label: 'In progress', cls: 'bg-amber-500/15 text-amber-400' },
    pending_approval: { label: 'Awaiting approval', cls: 'bg-amber-500/15 text-amber-400' },
    completed: { label: 'Completed', cls: 'bg-green-500/15 text-green-400' },
    rejected: { label: 'Sent back', cls: 'bg-red-500/15 text-red-400' }
  };

  // === Month filter helpers ===
  const isInMonth = (rec, ym) => {
    const raw = rec?.date || rec?.startDate || rec?.completed_date || rec?.submittedAt || rec?.createdAt || '';
    if (!raw) return false;
    const s = String(raw);
    return s.slice(0, 7) === ym; // 'YYYY-MM' prefix match works for ISO strings
  };
  const sumHours = (list, key = 'arbeidstid') =>
    list.reduce((acc, r) => acc + (parseFloat(r?.[key]) || 0), 0);

  const monthWorkOrders = myWorkOrders.filter(o => isInMonth(o, selectedMonth));
  const monthInternal = myInternalOrders.filter(o => isInMonth(o, selectedMonth));
  const monthLeave = leaveRequests.filter(l => isInMonth(l, selectedMonth));

  const totalWorkHours = sumHours(myWorkOrders);
  const totalInternalHours = sumHours(myInternalOrders);
  const monthWorkHours = sumHours(monthWorkOrders);
  const monthInternalHours = sumHours(monthInternal);

  // Lists to display in tabs respect the "filter by selected month" toggle.
  const visibleWorkOrders = filterListsByMonth ? monthWorkOrders : myWorkOrders;
  const visibleInternalOrders = filterListsByMonth ? monthInternal : myInternalOrders;
  const visibleLeaveRequests = filterListsByMonth ? monthLeave : leaveRequests;

  // Reset pagination when the underlying visible list changes (filter / month / view-as / data reload).
  useEffect(() => { setWorkOrderPage(1); }, [filterListsByMonth, selectedMonth, viewAsEmployeeId, viewAllMode, myWorkOrders.length]);
  useEffect(() => { setInternalPage(1); }, [filterListsByMonth, selectedMonth, viewAsEmployeeId, viewAllMode, myInternalOrders.length]);
  useEffect(() => { setLeavePage(1); }, [filterListsByMonth, selectedMonth, viewAsEmployeeId, viewAllMode, leaveRequests.length]);

  const paginate = (list, page) => {
    const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * PAGE_SIZE;
    return { items: list.slice(start, start + PAGE_SIZE), page: safePage, totalPages };
  };

  const renderPager = (page, totalPages, setPage) => {
    if (totalPages <= 1) return null;
    return (
      <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-[var(--theme-border,rgba(255,255,255,0.08))]">
        <button
          type="button"
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page <= 1}
          className="fm-btn fm-btn-secondary fm-btn-sm disabled:opacity-40"
        >
          ← Prev
        </button>
        <span className="text-xs fm-text-secondary">
          Page {page} / {totalPages}
        </span>
        <button
          type="button"
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages}
          className="fm-btn fm-btn-secondary fm-btn-sm disabled:opacity-40"
        >
          Next →
        </button>
      </div>
    );
  };

  const workOrderPaging = paginate(visibleWorkOrders, workOrderPage);
  const internalPaging = paginate(visibleInternalOrders, internalPage);
  const leavePaging = paginate(visibleLeaveRequests, leavePage);

  // === "View as" picker — visible for admins/owners or when no clear match ===
  const userRole = (user?.role || '').toLowerCase();
  const canViewOthers = ['owner', 'admin', 'system_admin'].includes(userRole);
  const noEmailMatch = !currentEmployee || currentEmployee.isVirtual;
  const showEmployeePicker = employees.length > 0 && (canViewOthers || noEmailMatch);
  const sortedEmployees = [...employees].sort((a, b) =>
    String(a.navn || a.name || '').localeCompare(String(b.navn || b.name || ''))
  );

  // Reusable month/total summary card rendered above lists.
  const renderMonthSummary = (label, monthCount, totalCount, monthHours, totalHours) => (
    <div className="fm-card mb-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs fm-text-secondary uppercase tracking-wide">{label}</p>
          <p className="text-sm fm-text-primary mt-0.5">
            <span className="font-semibold">{monthCount}</span> this month
            <span className="fm-text-secondary"> · </span>
            <span className="font-semibold">{totalCount}</span> total
          </p>
          {(monthHours > 0 || totalHours > 0) && (
            <p className="text-xs fm-text-secondary mt-0.5">
              {monthHours.toFixed(1)} h this month · {totalHours.toFixed(1)} h total
            </p>
          )}
        </div>
        <input
          type="month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          className="fm-input fm-input-sm"
          style={{ maxWidth: 160 }}
        />
      </div>
      <label className="flex items-center gap-2 text-xs fm-text-secondary mt-2 cursor-pointer">
        <input
          type="checkbox"
          checked={filterListsByMonth}
          onChange={(e) => setFilterListsByMonth(e.target.checked)}
        />
        Show only entries from selected month
      </label>
    </div>
  );

  // === Customers tab — search + helpers ===
  // Build a facility lookup map (id -> facility, anleggsnr -> facility) so we can
  // enrich customers with their linked facility info (anleggsnr lives on facilities).
  const facilityById = facilities.reduce((acc, f) => {
    if (f.id) acc[String(f.id)] = f;
    return acc;
  }, {});
  const facilityByAnleggsnr = facilities.reduce((acc, f) => {
    if (f.anleggsnr) acc[String(f.anleggsnr)] = f;
    return acc;
  }, {});

  const getCustomerFacility = (c) => {
    if (!c) return null;
    if (c.anlegg_id && facilityById[String(c.anlegg_id)]) return facilityById[String(c.anlegg_id)];
    if (c.anleggsnr && facilityByAnleggsnr[String(c.anleggsnr)]) return facilityByAnleggsnr[String(c.anleggsnr)];
    return null;
  };

  const getCustomerAnleggsnr = (c) => {
    if (c?.anleggsnr) return c.anleggsnr;
    const f = getCustomerFacility(c);
    return f?.anleggsnr || '';
  };

  // Union of customers + standalone facilities (facilities not already linked to a customer
  // appear as their own searchable rows so any anleggsnr can be found).
  const linkedFacilityKeys = new Set(
    customers.flatMap(c => [
      c.anlegg_id ? String(c.anlegg_id) : null,
      c.anleggsnr ? String(c.anleggsnr) : null
    ]).filter(Boolean)
  );
  const standaloneFacilities = facilities
    .filter(f => !(f.id && linkedFacilityKeys.has(String(f.id))) &&
                !(f.anleggsnr && linkedFacilityKeys.has(String(f.anleggsnr))))
    .map(f => ({
      // Normalise a facility into a customer-shaped row for the list/expansion.
      id: f.id ? `facility:${f.id}` : `facility:${f.anleggsnr}`,
      _isFacilityOnly: true,
      kundnavn: f.kundnavn || f.anleggsnavn || `Facility ${f.anleggsnr || ''}`.trim(),
      anleggsnr: f.anleggsnr,
      anlegg_id: f.id,
      adresse: f.anlegg_adresse,
      postnr: f.anlegg_postnr,
      poststed: f.anlegg_poststed,
      telefon: f.telefon || f.anlegg_telefon,
      epost: f.epost || f.anlegg_epost,
      anleggstype: f.anleggstype,
      typenr: f.typenr
    }));
  const allRows = [...customers, ...standaloneFacilities];

  const filteredCustomers = customerSearch.trim()
    ? allRows.filter(c => {
        const q = customerSearch.toLowerCase();
        const anr = String(getCustomerAnleggsnr(c) || '').toLowerCase();
        const fac = getCustomerFacility(c) || (c._isFacilityOnly ? c : null);
        return (
          c.kundnavn?.toLowerCase().includes(q) ||
          c.kundennr?.toString().toLowerCase().includes(q) ||
          anr.includes(q) ||
          c.adresse?.toLowerCase().includes(q) ||
          c.poststed?.toLowerCase().includes(q) ||
          c.telefon?.toString().toLowerCase().includes(q) ||
          c.epost?.toLowerCase().includes(q) ||
          fac?.anlegg_adresse?.toLowerCase().includes(q) ||
          fac?.anleggstype?.toLowerCase().includes(q)
        );
      }).slice(0, 50)
    : allRows.slice(0, 30);

  const startWorkOrderForCustomer = (customer) => {
    resetWorkOrderForm();
    const anr = getCustomerAnleggsnr(customer);
    setWorkOrderForm(f => ({
      ...f,
      customerType: 'existing',
      anleggsnr: anr || '',
      customer_id: customer.id || ''
    }));
    const typenr = customer.typenr || getCustomerFacility(customer)?.typenr;
    if (typenr) {
      const service = services.find(s => String(s.tjenestenr) === String(typenr));
      if (service) {
        setServicePricing(service);
        setWorkOrderForm(f => ({
          ...f,
          service_id: service.id,
          service_name: service.tjeneste_navn,
          price_type: 'pris'
        }));
        setServiceSearch(service.tjeneste_navn);
      }
    }
    setActiveTab('logtime');
    setShowTimeModal(true);
  };

  if (loading) {
    return (
      <div className="fm-page flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-[#3399ff] border-t-transparent rounded-full mx-auto mb-3"></div>
          <p className="fm-text-secondary text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="ess-page" className="fm-page pb-20">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-lg font-semibold fm-text-primary">Employee Self-Service</h1>
        <p className="fm-text-secondary text-sm mt-1">
          {currentEmployee ? `Welcome, ${currentEmployee.navn || currentEmployee.name || ''}` : 'Manage your time, leave, and tasks'}
        </p>
      </div>

      {/* "View as" employee picker (owners/admins or unmatched users) */}
      {showEmployeePicker && (
        <div className="fm-card mb-3">
          <label className="text-xs fm-text-secondary uppercase tracking-wide block mb-1">
            {canViewOthers ? 'View as employee' : 'Select your employee record'}
          </label>
          <div className="flex gap-2">
            <select
              value={viewAsEmployeeId || (currentEmployee?.id ? String(currentEmployee.id) : '')}
              onChange={(e) => setViewAsEmployeeId(e.target.value)}
              className="fm-input flex-1"
            >
              <option value="">— Auto (match by email) —</option>
              {sortedEmployees.map(e => (
                <option key={e.id} value={e.id}>
                  {e.navn || e.name || e.epost || e.email || e.id}
                  {e.epost ? ` (${e.epost})` : ''}
                </option>
              ))}
            </select>
            {viewAsEmployeeId && (
              <button
                type="button"
                onClick={() => setViewAsEmployeeId('')}
                className="fm-btn fm-btn-secondary fm-btn-sm"
                title="Clear override"
              >
                <X size={14} />
              </button>
            )}
          </div>
          {noEmailMatch && !viewAsEmployeeId && (
            <p className="text-xs fm-text-secondary mt-2">
              No employee record matched <span className="fm-text-primary">{user?.email}</span>.
              Pick your record above to load your data.
            </p>
          )}
          {canViewOthers && (
            <label className="flex items-center gap-2 text-xs fm-text-secondary mt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={viewAllMode}
                onChange={(e) => setViewAllMode(e.target.checked)}
              />
              👁 Admin view — show ALL employees' entries
            </label>
          )}
        </div>
      )}

      {/* Tab Navigation - Mobile optimized */}
      <div className="flex gap-1 mb-4 overflow-x-auto pb-2 -mx-4 px-4">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-[#3399ff] text-white'
                  : 'fm-bg-secondary fm-text-secondary hover:fm-text-primary'
              }`}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* === Home / My Portal === */}
      {activeTab === 'home' && (
        <div className="space-y-4">
          <div className="fm-card">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs fm-text-secondary uppercase tracking-wide">My Portal</p>
                <h2 className="text-xl font-semibold fm-text-primary mt-0.5">
                  {currentEmployee?.navn || user?.email || 'Welcome'}
                </h2>
                {currentEmployee?.stilling && (
                  <p className="text-sm fm-text-secondary mt-1">{currentEmployee.stilling}</p>
                )}
              </div>
              <button
                onClick={logout}
                className="fm-btn fm-btn-secondary fm-btn-sm flex items-center gap-1.5"
                title="Sign out"
              >
                <LogOut size={14} />
                Sign out
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setActiveTab('assigned')}
              className="fm-card text-left hover:border-[var(--theme-accent)] transition-colors"
            >
              <div className="flex items-center justify-between">
                <ClipboardCheck size={20} className="fm-text-accent" />
                <span className="text-2xl font-semibold fm-text-primary">{openAssignedCount}</span>
              </div>
              <p className="text-xs fm-text-secondary mt-2">Assigned tasks</p>
            </button>
            <button
              onClick={() => setActiveTab('workorders')}
              className="fm-card text-left hover:border-[var(--theme-accent)] transition-colors"
            >
              <div className="flex items-center justify-between">
                <Send size={20} className="fm-text-accent" />
                <span className="text-2xl font-semibold fm-text-primary">{pendingDispatchCount}</span>
              </div>
              <p className="text-xs fm-text-secondary mt-2">Open dispatches</p>
            </button>
            <button
              onClick={() => setActiveTab('logtime')}
              className="fm-card text-left hover:border-[var(--theme-accent)] transition-colors"
            >
              <div className="flex items-center justify-between">
                <Clock size={20} className="fm-text-accent" />
                <span className="text-2xl font-semibold fm-text-primary">{pendingTimeCount}</span>
              </div>
              <p className="text-xs fm-text-secondary mt-2">Pending work orders</p>
            </button>
            <button
              onClick={() => setActiveTab('leave')}
              className="fm-card text-left hover:border-[var(--theme-accent)] transition-colors"
            >
              <div className="flex items-center justify-between">
                <Calendar size={20} className="fm-text-accent" />
                <span className="text-2xl font-semibold fm-text-primary">{pendingLeaveCount}</span>
              </div>
              <p className="text-xs fm-text-secondary mt-2">Pending leave requests</p>
            </button>
            <button
              onClick={() => setActiveTab('customers')}
              className="fm-card text-left hover:border-[var(--theme-accent)] transition-colors"
            >
              <div className="flex items-center justify-between">
                <Users size={20} className="fm-text-accent" />
                <span className="text-2xl font-semibold fm-text-primary">{customers.length}</span>
              </div>
              <p className="text-xs fm-text-secondary mt-2">Customers / facilities</p>
            </button>
          </div>

          <div className="fm-card">
            <h3 className="font-medium fm-text-primary mb-3">Quick actions</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                onClick={() => { resetWorkOrderForm(); setActiveTab('logtime'); setShowTimeModal(true); }}
                className="fm-btn fm-btn-primary justify-center"
              >
                <Plus size={16} /> New work order
              </button>
              <button
                onClick={() => { setActiveTab('internal'); setShowInternalModal(true); }}
                className="fm-btn fm-btn-secondary justify-center"
              >
                <Briefcase size={16} /> Log internal
              </button>
              <button
                onClick={() => { setActiveTab('leave'); setShowLeaveModal(true); }}
                className="fm-btn fm-btn-secondary justify-center"
              >
                <Calendar size={16} /> Request leave
              </button>
              <button
                onClick={() => { setActiveTab('docs'); setShowDocModal(true); }}
                className="fm-btn fm-btn-secondary justify-center"
              >
                <Camera size={16} /> Add documentation
              </button>
            </div>
          </div>

          {receivedDispatches.length > 0 && (
            <div className="fm-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium fm-text-primary">Latest dispatches</h3>
                <button
                  onClick={() => setActiveTab('workorders')}
                  className="text-xs fm-text-accent flex items-center gap-1"
                >
                  View all <ChevronRight size={12} />
                </button>
              </div>
              <div className="space-y-2">
                {receivedDispatches.slice(0, 3).map(d => (
                  <div key={d.id} className="p-3 fm-bg-secondary rounded-lg">
                    <p className="text-sm font-medium fm-text-primary">{d.title || d.subject || 'Dispatch'}</p>
                    {d.customerName && (
                      <p className="text-xs fm-text-secondary mt-0.5">{d.customerName}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {latestPayslip && (
            <div className="fm-card">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium fm-text-primary">Latest payslip</h3>
                  <p className="text-xs fm-text-secondary mt-0.5">{latestPayslip.period || latestPayslip.date}</p>
                </div>
                <button
                  onClick={() => setActiveTab('payslips')}
                  className="fm-btn fm-btn-sm fm-btn-secondary"
                >
                  View
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* === Assigned Tasks === */}
      {activeTab === 'assigned' && (
        <div className="space-y-3">
          <div className="fm-card">
            <h2 className="text-lg font-semibold fm-text-primary flex items-center gap-2">
              <ClipboardCheck size={18} className="fm-text-accent" /> My assigned tasks
            </h2>
            <p className="text-xs fm-text-secondary mt-1">
              Work orders sent to you. Start a task, then mark it done to send it for approval.
            </p>
          </div>
          {assignedTasks.length === 0 ? (
            <div className="fm-card text-center py-8">
              <ClipboardCheck size={32} className="mx-auto fm-text-secondary opacity-50" />
              <p className="fm-text-secondary text-sm mt-2">No tasks assigned to you yet.</p>
            </div>
          ) : (
            [...assignedTasks]
              .sort((a, b) => {
                const rank = (s) => (s === 'completed' || s === 'rejected') ? 1 : 0;
                const r = rank(a.status) - rank(b.status);
                if (r !== 0) return r;
                return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
              })
              .map(task => {
                const meta = TASK_STATUS_META[task.status] || TASK_STATUS_META.assigned;
                const overdue = isTaskOverdue(task);
                const busy = taskBusyId === task.id;
                return (
                  <div key={task.id} className={`fm-card border-l-4 ${overdue ? 'border-l-red-500' : task.status === 'completed' ? 'border-l-green-500' : 'border-l-[#3399ff]'}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium fm-text-primary">{task.workOrderTitle}</h3>
                      <span className={`px-2 py-0.5 rounded text-xs ${meta.cls}`}>{meta.label}</span>
                      {overdue && <span className="px-2 py-0.5 rounded text-xs bg-red-500/15 text-red-400">Overdue</span>}
                    </div>
                    {task.description && (
                      <p className="text-sm fm-text-secondary mt-1 whitespace-pre-wrap">{task.description}</p>
                    )}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs fm-text-secondary">
                      {task.customerName && <span><MapPin size={11} className="inline mr-1" />{task.customerName}</span>}
                      {task.facilityNumber && <span>Facility #{task.facilityNumber}</span>}
                      {task.customerAddress && <span>{task.customerAddress}</span>}
                      {task.customerPhone && <span><Phone size={11} className="inline mr-1" />{task.customerPhone}</span>}
                      {task.dueDate && (
                        <span className={overdue ? 'text-red-400' : ''}>
                          <Calendar size={11} className="inline mr-1" />Due {new Date(task.dueDate).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    {task.rejectionReason && task.status !== 'completed' && (
                      <p className="text-xs text-red-400 mt-2">Sent back by manager: {task.rejectionReason}</p>
                    )}
                    <div className="flex gap-2 mt-3">
                      {(task.status === 'assigned' || task.status === 'rejected') && (
                        <button
                          disabled={busy}
                          onClick={() => updateTaskStatus(task, 'in_progress')}
                          className="fm-btn fm-btn-primary fm-btn-sm flex items-center gap-1 disabled:opacity-50"
                        >
                          <PlayCircle size={14} /> Start
                        </button>
                      )}
                      {task.status === 'in_progress' && (
                        <button
                          disabled={busy}
                          onClick={() => updateTaskStatus(task, 'pending_approval')}
                          className="fm-btn fm-btn-primary fm-btn-sm flex items-center gap-1 disabled:opacity-50"
                        >
                          <CheckCircle size={14} /> Mark done
                        </button>
                      )}
                      {task.status === 'pending_approval' && (
                        <span className="text-xs fm-text-secondary flex items-center gap-1"><Info size={12} /> Waiting for manager approval</span>
                      )}
                      {task.status === 'completed' && (
                        <span className="text-xs text-green-400 flex items-center gap-1"><CheckCircle size={12} /> Approved &amp; completed</span>
                      )}
                    </div>
                    {Array.isArray(task.activityLog) && task.activityLog.length > 0 && (
                      <details className="mt-3">
                        <summary className="text-xs fm-text-secondary cursor-pointer">Activity ({task.activityLog.length})</summary>
                        <div className="mt-2 space-y-1">
                          {[...task.activityLog].reverse().map((l, i) => (
                            <div key={i} className="text-xs fm-text-secondary">
                              <span className="fm-text-primary">{l.text}</span> {'\u2014'} {l.user}{l.timestamp ? `, ${new Date(l.timestamp).toLocaleString()}` : ''}
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                );
              })
          )}
        </div>
      )}

      {/* === Customers / Facilities === */}
      {activeTab === 'customers' && (
        <div className="space-y-3">
          <div className="fm-card">
            <label className="fm-label">Search customers / facilities</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 fm-text-secondary pointer-events-none" />
              <input
                type="search"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                placeholder="Name, anleggsnr, address, phone, email…"
                className="fm-input"
                style={{ paddingLeft: 44 }}
              />
            </div>
            <p className="text-xs fm-text-secondary mt-2">
              Showing {filteredCustomers.length} of {allRows.length}
              {facilities.length > 0 && ` · ${customers.length} customers + ${standaloneFacilities.length} unlinked facilities`}
            </p>
          </div>

          {filteredCustomers.length === 0 ? (
            <div className="fm-card text-center py-6">
              <Info size={24} className="mx-auto mb-2 fm-text-secondary" />
              <p className="text-sm fm-text-secondary">No customers match your search.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredCustomers.map(c => {
                const facility = getCustomerFacility(c);
                const anr = getCustomerAnleggsnr(c);
                const isOpen = expandedCustomerId === (c.id || anr);
                const addressLine = [c.adresse, c.postnr, c.poststed].filter(Boolean).join(', ');
                const facilityAddress = facility ? [facility.anlegg_adresse, facility.anlegg_postnr, facility.anlegg_poststed].filter(Boolean).join(', ') : '';
                // All non-empty primitive fields for the full-detail dump
                const detailRows = Object.entries({ ...(c || {}), ...(facility ? { _facility: facility } : {}) })
                  .filter(([k, v]) =>
                    k !== 'id' && k !== '_facility' &&
                    v !== null && v !== undefined && v !== '' &&
                    (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')
                  );
                const facilityRows = facility ? Object.entries(facility).filter(([k, v]) =>
                  k !== 'id' && v !== null && v !== undefined && v !== '' &&
                  (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')
                ) : [];
                return (
                  <div key={c.id || anr} className="fm-card">
                    <button
                      type="button"
                      onClick={() => setExpandedCustomerId(isOpen ? null : (c.id || anr))}
                      className="w-full flex items-start justify-between gap-2 text-left"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium fm-text-primary truncate">{c.kundnavn || `Facility #${anr}`}</p>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs fm-text-secondary mt-0.5">
                          {anr && <span>Anleggsnr: {anr}</span>}
                          {c.kundennr && <span>Kundenr: {c.kundennr}</span>}
                          {facility?.anleggstype && <span>{facility.anleggstype}</span>}
                        </div>
                      </div>
                      <ChevronRight
                        size={18}
                        className="fm-text-secondary shrink-0 transition-transform"
                        style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}
                      />
                    </button>

                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); startWorkOrderForCustomer(c); }}
                        className="fm-btn fm-btn-sm fm-btn-primary"
                        title="Log work for this customer"
                      >
                        <Plus size={14} /> Log work
                      </button>
                      {addressLine && (
                        <a
                          onClick={(e) => e.stopPropagation()}
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressLine)}`}
                          target="_blank" rel="noopener noreferrer"
                          className="fm-btn fm-btn-sm fm-btn-secondary"
                        >
                          <MapPin size={14} /> Map
                        </a>
                      )}
                      {c.telefon && (
                        <a
                          onClick={(e) => e.stopPropagation()}
                          href={`tel:${c.telefon}`}
                          className="fm-btn fm-btn-sm fm-btn-secondary"
                        >
                          <Phone size={14} /> Call
                        </a>
                      )}
                      {c.epost && (
                        <a
                          onClick={(e) => e.stopPropagation()}
                          href={`mailto:${c.epost}`}
                          className="fm-btn fm-btn-sm fm-btn-secondary"
                        >
                          <Mail size={14} /> Email
                        </a>
                      )}
                    </div>

                    {isOpen && (
                      <div className="mt-3 pt-3 border-t border-[var(--theme-border)] space-y-3">
                        {(addressLine || facilityAddress) && (
                          <div>
                            <p className="text-xs fm-text-secondary uppercase tracking-wide mb-1">Address</p>
                            {addressLine && <p className="text-sm fm-text-primary">{addressLine}</p>}
                            {facilityAddress && facilityAddress !== addressLine && (
                              <p className="text-sm fm-text-secondary mt-0.5">Facility: {facilityAddress}</p>
                            )}
                          </div>
                        )}

                        <div>
                          <p className="text-xs fm-text-secondary uppercase tracking-wide mb-1">Customer details</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                            {detailRows.map(([k, v]) => (
                              <div key={k} className="flex flex-col gap-0.5 text-xs sm:flex-row sm:items-start sm:justify-between sm:gap-2">
                                <span className="fm-text-secondary capitalize break-words">{k.replace(/_/g, ' ')}</span>
                                <span className="fm-text-primary break-words whitespace-normal sm:max-w-[60%] sm:text-right" title={String(v)}>{String(v)}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {facilityRows.length > 0 && (
                          <div>
                            <p className="text-xs fm-text-secondary uppercase tracking-wide mb-1">Facility details</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                              {facilityRows.map(([k, v]) => (
                                <div key={k} className="flex flex-col gap-0.5 text-xs sm:flex-row sm:items-start sm:justify-between sm:gap-2">
                                  <span className="fm-text-secondary capitalize break-words">{k.replace(/_/g, ' ')}</span>
                                  <span className="fm-text-primary break-words whitespace-normal sm:max-w-[60%] sm:text-right" title={String(v)}>{String(v)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {!facility && anr && (
                          <p className="text-xs fm-text-secondary italic">No matching facility record found for anleggsnr {anr}.</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Log Time Tab */}
      {activeTab === 'logtime' && (
        <div className="space-y-4">
          {renderMonthSummary('Work orders', monthWorkOrders.length, myWorkOrders.length, monthWorkHours, totalWorkHours)}
          {/* Quick Add Button */}
          <button
            onClick={() => { resetWorkOrderForm(); setShowTimeModal(true); }}
            className="w-full fm-btn fm-btn-primary flex items-center justify-center gap-2 py-3"
          >
            <Plus size={18} />
            Register Work Order
          </button>

          {/* My Submitted Work Orders */}
          <div className="fm-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium fm-text-primary">My Submissions</h3>
              <span className="text-xs fm-text-secondary">{visibleWorkOrders.length} total</span>
            </div>
            {visibleWorkOrders.length === 0 ? (
              <p className="fm-text-secondary text-sm text-center py-4">{filterListsByMonth ? 'No work orders in selected month' : 'No work orders submitted yet'}</p>
            ) : (
              <>
                <div className="space-y-2">
                  {workOrderPaging.items.map(order => (
                    <div key={order.id} className="flex items-center justify-between p-3 fm-bg-secondary rounded-lg">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium fm-text-primary truncate">{getCustomerName(order)}</span>
                          {getStatusBadge(order.status)}
                        </div>
                        <div className="flex items-center gap-3 text-xs fm-text-secondary">
                          <span>{new Date(order.date).toLocaleDateString('no-NO')}</span>
                          <span>{order.arbeidstid || 0}h work</span>
                          {order.service_name && <span>{order.service_name}</span>}
                        </div>
                      </div>
                      <ChevronRight size={16} className="fm-text-secondary flex-shrink-0" />
                    </div>
                  ))}
                </div>
                {renderPager(workOrderPaging.page, workOrderPaging.totalPages, setWorkOrderPage)}
              </>
            )}
          </div>

          {/* Weekly Summary */}
          <div className="fm-card">
            <h3 className="font-medium fm-text-primary mb-3">This Week</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 fm-bg-secondary rounded-lg text-center">
                <p className="text-2xl font-bold fm-text-accent">
                  {myWorkOrders
                    .filter(o => {
                      const orderDate = new Date(o.date);
                      const now = new Date();
                      const weekAgo = new Date(now.setDate(now.getDate() - 7));
                      return orderDate >= weekAgo;
                    })
                    .reduce((sum, o) => sum + (parseFloat(o.arbeidstid) || 0), 0)
                    .toFixed(1)}h
                </p>
                <p className="text-xs fm-text-secondary">Hours Logged</p>
              </div>
              <div className="p-3 fm-bg-secondary rounded-lg text-center">
                <p className="text-2xl font-bold text-yellow-400">
                  {myWorkOrders.filter(o => o.status === 'pending').length}
                </p>
                <p className="text-xs fm-text-secondary">Pending Approval</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Internal Work Tab */}
      {activeTab === 'internal' && (
        <div className="space-y-4">
          {renderMonthSummary('Internal hours', monthInternal.length, myInternalOrders.length, monthInternalHours, totalInternalHours)}
          <button
            onClick={() => { resetInternalForm(); setShowInternalModal(true); }}
            className="w-full fm-btn fm-btn-primary flex items-center justify-center gap-2 py-3"
          >
            <Plus size={18} />
            Log Internal Work
          </button>

          {/* My Internal Work Submissions */}
          <div className="fm-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium fm-text-primary">My Internal Work</h3>
              <span className="text-xs fm-text-secondary">{visibleInternalOrders.length} total</span>
            </div>
            {visibleInternalOrders.length === 0 ? (
              <p className="fm-text-secondary text-sm text-center py-4">{filterListsByMonth ? 'No internal work in selected month' : 'No internal work logged yet'}</p>
            ) : (
              <>
                <div className="space-y-2">
                  {internalPaging.items.map(order => (
                    <div key={order.id} className="flex items-center justify-between p-3 fm-bg-secondary rounded-lg">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium fm-text-primary truncate">{getTaskTypeLabel(order.task_type)}</span>
                          {getStatusBadge(order.status)}
                        </div>
                        <div className="flex items-center gap-3 text-xs fm-text-secondary">
                          <span>{new Date(order.date).toLocaleDateString('no-NO')}</span>
                          <span>{order.arbeidstid || 0}h</span>
                          {order.avdeling && <span>{order.avdeling}</span>}
                        </div>
                        {order.beskrivelse && (
                          <p className="text-xs fm-text-secondary mt-1 truncate">{order.beskrivelse}</p>
                        )}
                      </div>
                      <ChevronRight size={16} className="fm-text-secondary flex-shrink-0" />
                    </div>
                  ))}
                </div>
                {renderPager(internalPaging.page, internalPaging.totalPages, setInternalPage)}
              </>
            )}
          </div>

          {/* Internal Weekly Summary */}
          <div className="fm-card">
            <h3 className="font-medium fm-text-primary mb-3">This Week (Internal)</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 fm-bg-secondary rounded-lg text-center">
                <p className="text-2xl font-bold fm-text-accent">
                  {myInternalOrders
                    .filter(o => {
                      const orderDate = new Date(o.date);
                      const now = new Date();
                      const weekAgo = new Date(now.setDate(now.getDate() - 7));
                      return orderDate >= weekAgo;
                    })
                    .reduce((sum, o) => sum + (parseFloat(o.arbeidstid) || 0), 0)
                    .toFixed(1)}h
                </p>
                <p className="text-xs fm-text-secondary">Internal Hours</p>
              </div>
              <div className="p-3 fm-bg-secondary rounded-lg text-center">
                <p className="text-2xl font-bold text-yellow-400">
                  {myInternalOrders.filter(o => o.status === 'pending').length}
                </p>
                <p className="text-xs fm-text-secondary">Pending Approval</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Leave Tab */}
      {activeTab === 'leave' && (
        <div className="space-y-4">
          {renderMonthSummary('Leave requests', monthLeave.length, leaveRequests.length, 0, 0)}
          <button
            onClick={() => setShowLeaveModal(true)}
            className="w-full fm-btn fm-btn-primary flex items-center justify-center gap-2 py-3"
          >
            <Plus size={18} />
            Request Leave
          </button>

          {/* Leave Requests */}
          <div className="fm-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium fm-text-primary">My Leave Requests</h3>
              <span className="text-xs fm-text-secondary">{visibleLeaveRequests.length} total</span>
            </div>
            {visibleLeaveRequests.length === 0 ? (
              <p className="fm-text-secondary text-sm text-center py-4">{filterListsByMonth ? 'No leave requests in selected month' : 'No leave requests'}</p>
            ) : (
              <>
                <div className="space-y-2">
                  {leavePaging.items.map(req => (
                    <div key={req.id} className="p-3 fm-bg-secondary rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium fm-text-primary">{getLeaveTypeLabel(req.type)}</span>
                        {getStatusBadge(req.status)}
                      </div>
                      <div className="text-xs fm-text-secondary mb-2">
                        {new Date(req.startDate).toLocaleDateString('no-NO')} - {new Date(req.endDate).toLocaleDateString('no-NO')}
                      </div>
                      {req.reason && (
                        <p className="text-sm fm-text-secondary">{req.reason}</p>
                      )}
                      {req.status === 'pending' && (
                        <button
                          onClick={() => handleDeleteLeave(req.id)}
                          className="mt-2 text-xs text-red-400 flex items-center gap-1"
                        >
                          <Trash2 size={12} /> Cancel Request
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {renderPager(leavePaging.page, leavePaging.totalPages, setLeavePage)}
              </>
            )}
          </div>
        </div>
      )}

      {/* My Tasks/Work Orders Tab */}
      {activeTab === 'workorders' && (
        <div className="space-y-4">
          <div className="fm-card">
            <h3 className="font-medium fm-text-primary mb-3">Assigned Work Orders</h3>
            {receivedDispatches.length === 0 ? (
              <p className="fm-text-secondary text-sm text-center py-4">No work orders assigned to you</p>
            ) : (
              <div className="space-y-2">
                {receivedDispatches.map(order => (
                  <div key={order.id} className="p-3 fm-bg-secondary rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium fm-text-primary">{order.jobTitle}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        order.priority === 'high' ? 'bg-red-500/20 text-red-400' :
                        order.priority === 'low' ? 'bg-green-500/20 text-green-400' :
                        'bg-blue-500/20 text-blue-400'
                      }`}>
                        {order.priority}
                      </span>
                    </div>
                    <p className="text-sm fm-text-secondary mb-2">{order.jobDescription}</p>
                    <div className="text-xs fm-text-secondary space-y-1">
                      <div><strong>Customer:</strong> {order.customerName}</div>
                      {order.customerAddress && <div><strong>Address:</strong> {order.customerAddress}</div>}
                      {order.scheduledDate && <div><strong>Date:</strong> {new Date(order.scheduledDate).toLocaleDateString('no-NO')}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'docs' && (
        <div className="space-y-4">
          <button
            onClick={() => setShowDocModal(true)}
            className="w-full fm-btn fm-btn-primary flex items-center justify-center gap-2 py-3"
          >
            <Camera size={18} />
            Document Work
          </button>

          <div className="fm-card">
            <h3 className="font-medium fm-text-primary mb-3">My Documents</h3>
            {workDocuments.filter(d => String(d.employee_id) === String(currentEmployee?.id)).length === 0 ? (
              <p className="fm-text-secondary text-sm text-center py-4">No documents uploaded</p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {workDocuments
                  .filter(d => String(d.employee_id) === String(currentEmployee?.id))
                  .slice(0, 10)
                  .map(doc => (
                    <div key={doc.id} className="p-2 fm-bg-secondary rounded-lg">
                      {doc.imageData ? (
                        <img 
                          src={doc.imageData} 
                          alt={doc.title}
                          className="w-full h-24 object-cover rounded mb-2"
                        />
                      ) : (
                        <div className="w-full h-24 fm-bg-tertiary rounded mb-2 flex items-center justify-center">
                          <Image size={24} className="fm-text-secondary" />
                        </div>
                      )}
                      <p className="text-xs font-medium fm-text-primary truncate">{doc.title}</p>
                      <p className="text-xs fm-text-secondary">{new Date(doc.uploadedAt).toLocaleDateString('no-NO')}</p>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Payslips Tab */}
      {activeTab === 'payslips' && (
        <div className="space-y-3">
          {payslips.length === 0 ? (
            <div className="fm-card text-center py-8">
              <FileText size={48} className="mx-auto mb-3 fm-text-secondary" />
              <h3 className="font-medium fm-text-primary mb-2">No payslips yet</h3>
              <p className="fm-text-secondary text-sm">
                Once your manager approves a payslip it will show up here.
              </p>
            </div>
          ) : (
            <>
              <h3 className="font-medium fm-text-primary px-1">Your payslips</h3>
              {[...payslips]
                .sort((a, b) => String(b.generated_at || b.date || '').localeCompare(String(a.generated_at || a.date || '')))
                .map(slip => {
                  const isOpen = expandedPayslipId === slip.id;
                  const period = slip.period_from && slip.period_to
                    ? `${slip.period_from} → ${slip.period_to}`
                    : (slip.period || slip.date || '');
                  return (
                    <div key={slip.id} className="fm-card">
                      <button
                        type="button"
                        onClick={() => setExpandedPayslipId(isOpen ? null : slip.id)}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <div>
                          <div className="font-medium fm-text-primary">{period}</div>
                          <div className="text-xs fm-text-secondary mt-0.5">
                            {slip.lines?.length || 0} items
                            {slip.totals?.hours ? ` · ${slip.totals.hours.toFixed(1)}h` : ''}
                            {slip.totals?.km ? ` · ${slip.totals.km.toFixed(0)}km` : ''}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-green-400">
                            {(slip.gross_amount || 0).toLocaleString()} {slip.currency || 'kr'}
                          </div>
                          <div className="text-xs fm-text-secondary mt-0.5">
                            {slip.approved_at ? new Date(slip.approved_at).toLocaleDateString() : ''}
                          </div>
                        </div>
                      </button>
                      {isOpen && (
                        <div className="mt-3 pt-3 border-t border-[var(--theme-border)] space-y-2">
                          {(slip.lines || []).map(l => (
                            <div key={l.key} className="flex items-start justify-between text-sm">
                              <div className="flex-1 pr-2">
                                <div className="fm-text-primary">{l.label}</div>
                                {l.sublabel && <div className="text-xs fm-text-secondary">{l.sublabel}</div>}
                                {l.date && <div className="text-xs fm-text-secondary">{new Date(l.date).toLocaleDateString()}</div>}
                              </div>
                              <div className="font-medium text-green-400 whitespace-nowrap">
                                {(l.amount || 0).toLocaleString()} {slip.currency || 'kr'}
                              </div>
                            </div>
                          ))}
                          {slip.note && (
                            <div className="text-xs fm-text-secondary italic pt-2 border-t border-[var(--theme-border)]">
                              {slip.note}
                            </div>
                          )}
                          <div className="flex items-center justify-between pt-2 border-t border-[var(--theme-border)] text-sm">
                            <span className="fm-text-secondary">Gross total</span>
                            <span className="font-bold text-green-400">
                              {(slip.gross_amount || 0).toLocaleString()} {slip.currency || 'kr'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
            </>
          )}
        </div>
      )}

      {/* Work Order Modal - Full form like Work Orders page */}
      {showTimeModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="fm-bg-primary w-full sm:max-w-lg sm:rounded-xl rounded-t-xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 fm-bg-primary p-4 border-b border-[var(--theme-border)] flex items-center justify-between z-10">
              <h2 className="text-lg font-semibold fm-text-primary">Register Work Order</h2>
              <button onClick={() => setShowTimeModal(false)} className="p-2">
                <X size={20} className="fm-text-secondary" />
              </button>
            </div>
            <form onSubmit={handleWorkOrderSubmit} className="p-4 pb-24 space-y-4">
              {/* Customer Type */}
              <div>
                <label className="fm-label">Customer Type</label>
                <div className="flex gap-4 mt-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="customerType"
                      value="existing"
                      checked={workOrderForm.customerType === 'existing'}
                      onChange={(e) => setWorkOrderForm({ ...workOrderForm, customerType: e.target.value, customName: '', anleggsnr: '', customer_id: '' })}
                      className="fm-radio"
                    />
                    <span className="text-sm fm-text-primary">Existing customer</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="customerType"
                      value="custom"
                      checked={workOrderForm.customerType === 'custom'}
                      onChange={(e) => { setWorkOrderForm({ ...workOrderForm, customerType: e.target.value, anleggsnr: '', customer_id: '' }); setServicePricing(null); }}
                      className="fm-radio"
                    />
                    <span className="text-sm fm-text-primary">Custom order</span>
                  </label>
                </div>
              </div>

              {/* Existing Customer - Facility Number */}
              {workOrderForm.customerType === 'existing' && (
                <div>
                  <label className="fm-label">Facility no *</label>
                  <input
                    type="text"
                    value={workOrderForm.anleggsnr}
                    onChange={(e) => handleAnleggsnrChange(e.target.value)}
                    placeholder="Enter facility number"
                    className="fm-input"
                    required
                  />
                  {workOrderForm.anleggsnr && getCurrentCustomer() && (
                    <div className="mt-2">
                      <p className="text-green-400 text-sm">✓ {getCurrentCustomer().kundnavn}</p>
                      {servicePricing && (
                        <div className="mt-1 text-sm fm-text-accent flex items-center gap-2">
                          <Info size={14} />
                          <span>Service: {servicePricing.tjeneste_navn}</span>
                        </div>
                      )}
                    </div>
                  )}
                  {workOrderForm.anleggsnr && !getCurrentCustomer() && (
                    <p className="text-red-400 text-sm mt-1">✗ Cannot find customer</p>
                  )}
                </div>
              )}

              {/* Custom Order - Name */}
              {workOrderForm.customerType === 'custom' && (
                <div>
                  <label className="fm-label">Custom Name *</label>
                  <input
                    type="text"
                    value={workOrderForm.customName}
                    onChange={(e) => setWorkOrderForm({ ...workOrderForm, customName: e.target.value })}
                    placeholder="Enter customer/order name"
                    className="fm-input"
                    required
                  />
                </div>
              )}

              {/* Date */}
              <div>
                <label className="fm-label">Date *</label>
                <input
                  type="date"
                  value={workOrderForm.date}
                  onChange={(e) => setWorkOrderForm({ ...workOrderForm, date: e.target.value })}
                  className="fm-input"
                  required
                />
              </div>

              {/* Service */}
              <div>
                <label className="fm-label">Service *</label>
                <div className="relative">
                  <input
                    type="text"
                    value={serviceSearch}
                    onChange={(e) => { setServiceSearch(e.target.value); setShowServiceDropdown(true); setWorkOrderForm({ ...workOrderForm, service_id: '', service_name: '', price_type: 'pris' }); }}
                    onFocus={() => setShowServiceDropdown(true)}
                    onBlur={() => setTimeout(() => setShowServiceDropdown(false), 200)}
                    placeholder="Search for service..."
                    className="fm-input"
                    required={!workOrderForm.service_id}
                  />
                  {showServiceDropdown && getFilteredServices().length > 0 && (
                    <div className="absolute z-50 w-full mt-1 fm-bg-card border fm-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {getFilteredServices().map(service => (
                        <button key={service.id} type="button" onClick={() => handleServiceSelect(service)} className="w-full px-3 py-2 text-left hover:fm-bg-hover fm-text-primary flex justify-between items-center">
                          <span>{service.tjeneste_navn}</span>
                          <span className="text-xs fm-text-secondary">#{service.tjenestenr}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {workOrderForm.service_name && (
                    <div className="mt-1 text-sm fm-text-accent flex items-center gap-2">
                      <span>Selected: {workOrderForm.service_name}</span>
                      <button type="button" onClick={() => { setWorkOrderForm({ ...workOrderForm, service_id: '', service_name: '', price_type: 'pris' }); setServiceSearch(''); setServicePricing(null); }} className="text-xs hover:text-red-400">✕</button>
                    </div>
                  )}
                </div>
              </div>

              {/* Price Type */}
              {workOrderForm.service_id && getPriceOptions().length > 0 && (
                <div>
                  <label className="fm-label">Price Type *</label>
                  <select value={workOrderForm.price_type} onChange={(e) => setWorkOrderForm({ ...workOrderForm, price_type: e.target.value })} className="fm-select" required>
                    {getPriceOptions().map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </div>
              )}

              {/* Supplier */}
              <div>
                <label className="fm-label">Supplier</label>
                <select value={workOrderForm.supplier_id} onChange={(e) => setWorkOrderForm({ ...workOrderForm, supplier_id: e.target.value })} className="fm-select">
                  <option value="">No supplier selected</option>
                  {suppliers.map(supplier => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}
                </select>
              </div>

              {/* Time inputs */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="fm-label">Work hours</label>
                  <input type="number" step="0.5" value={workOrderForm.arbeidstid} onChange={(e) => setWorkOrderForm({ ...workOrderForm, arbeidstid: e.target.value })} className="fm-input" />
                </div>
                <div>
                  <label className="fm-label">Drive hours</label>
                  <input type="number" step="0.5" value={workOrderForm.kjoretid} onChange={(e) => setWorkOrderForm({ ...workOrderForm, kjoretid: e.target.value })} className="fm-input" />
                </div>
                <div>
                  <label className="fm-label">Km driven</label>
                  <input type="number" step="1" value={workOrderForm.kjorte_km} onChange={(e) => setWorkOrderForm({ ...workOrderForm, kjorte_km: e.target.value })} className="fm-input" />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="fm-label">Description</label>
                <textarea value={workOrderForm.description} onChange={(e) => setWorkOrderForm({ ...workOrderForm, description: e.target.value })} className="fm-textarea" rows="3" placeholder="Work performed..." />
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowTimeModal(false)} className="flex-1 fm-btn fm-btn-secondary py-3">Cancel</button>
                <button type="submit" disabled={submitting} className={`flex-1 fm-btn fm-btn-primary py-3 ${submitting ? 'opacity-50' : ''}`}>
                  {submitting ? 'Submitting...' : 'Submit for Approval'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Internal Work Modal */}
      {showInternalModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="fm-bg-primary w-full sm:max-w-md sm:rounded-xl rounded-t-xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 fm-bg-primary p-4 border-b border-[var(--theme-border)] flex items-center justify-between z-10">
              <h2 className="text-lg font-semibold fm-text-primary">Log Internal Work</h2>
              <button onClick={() => setShowInternalModal(false)} className="p-2">
                <X size={20} className="fm-text-secondary" />
              </button>
            </div>
            <form onSubmit={handleInternalSubmit} className="p-4 pb-24 space-y-4">
              {/* Date */}
              <div>
                <label className="fm-label">Date *</label>
                <input
                  type="date"
                  value={internalForm.date}
                  onChange={(e) => setInternalForm({ ...internalForm, date: e.target.value })}
                  className="fm-input"
                  required
                />
              </div>

              {/* Task Type */}
              <div>
                <label className="fm-label">Work Type *</label>
                <select
                  value={internalForm.task_type}
                  onChange={(e) => setInternalForm({ ...internalForm, task_type: e.target.value })}
                  className="fm-select"
                  required
                >
                  <option value="kontor">Office Work</option>
                  <option value="montering">Installation</option>
                  <option value="vedlikehold">Maintenance</option>
                  <option value="reise">Travel</option>
                  <option value="diverse">Miscellaneous</option>
                </select>
              </div>

              {/* Department */}
              <div>
                <label className="fm-label">Department</label>
                <input
                  type="text"
                  value={internalForm.avdeling}
                  onChange={(e) => setInternalForm({ ...internalForm, avdeling: e.target.value })}
                  className="fm-input"
                  placeholder="e.g., Administration, Field"
                />
              </div>

              {/* Hours */}
              <div>
                <label className="fm-label">Hours *</label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  max="24"
                  value={internalForm.arbeidstid}
                  onChange={(e) => setInternalForm({ ...internalForm, arbeidstid: e.target.value })}
                  className="fm-input"
                  placeholder="e.g., 7.5"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="fm-label">Description *</label>
                <textarea
                  value={internalForm.beskrivelse}
                  onChange={(e) => setInternalForm({ ...internalForm, beskrivelse: e.target.value })}
                  className="fm-textarea"
                  rows="3"
                  placeholder="What internal work did you do?"
                  required
                />
              </div>

              {/* Comment */}
              <div>
                <label className="fm-label">Additional Notes</label>
                <textarea
                  value={internalForm.kommentar}
                  onChange={(e) => setInternalForm({ ...internalForm, kommentar: e.target.value })}
                  className="fm-textarea"
                  rows="2"
                  placeholder="Optional notes"
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowInternalModal(false)} className="flex-1 fm-btn fm-btn-secondary py-3">Cancel</button>
                <button type="submit" disabled={internalSubmitting} className={`flex-1 fm-btn fm-btn-primary py-3 ${internalSubmitting ? 'opacity-50' : ''}`}>
                  {internalSubmitting ? 'Submitting...' : 'Submit for Approval'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Leave Request Modal */}
      {showLeaveModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="fm-bg-primary w-full sm:max-w-md sm:rounded-xl rounded-t-xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 fm-bg-primary p-4 border-b border-[var(--theme-border)] flex items-center justify-between">
              <h2 className="text-lg font-semibold fm-text-primary">Request Leave</h2>
              <button onClick={() => setShowLeaveModal(false)} className="p-2">
                <X size={20} className="fm-text-secondary" />
              </button>
            </div>
            <form onSubmit={handleLeaveSubmit} className="p-4 pb-24 space-y-4">
              <div>
                <label className="fm-label">Leave Type *</label>
                <select
                  value={leaveForm.type}
                  onChange={e => setLeaveForm({ ...leaveForm, type: e.target.value })}
                  className="fm-select"
                  required
                >
                  <option value="vacation">Vacation</option>
                  <option value="sick">Sick Leave</option>
                  <option value="personal">Personal Leave</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="fm-label">Start Date *</label>
                  <input
                    type="date"
                    value={leaveForm.startDate}
                    onChange={e => setLeaveForm({ ...leaveForm, startDate: e.target.value })}
                    className="fm-input"
                    required
                  />
                </div>
                <div>
                  <label className="fm-label">End Date *</label>
                  <input
                    type="date"
                    value={leaveForm.endDate}
                    onChange={e => setLeaveForm({ ...leaveForm, endDate: e.target.value })}
                    className="fm-input"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="fm-label">Reason *</label>
                <textarea
                  value={leaveForm.reason}
                  onChange={e => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                  className="fm-textarea"
                  rows="2"
                  placeholder="Reason for leave"
                  required
                />
              </div>
              <div>
                <label className="fm-label">Additional Notes</label>
                <textarea
                  value={leaveForm.notes}
                  onChange={e => setLeaveForm({ ...leaveForm, notes: e.target.value })}
                  className="fm-textarea"
                  rows="2"
                  placeholder="Optional"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowLeaveModal(false)}
                  className="flex-1 fm-btn fm-btn-secondary py-3"
                >
                  Cancel
                </button>
                <button type="submit" className="flex-1 fm-btn fm-btn-primary py-3">
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Document Upload Modal */}
      {showDocModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="fm-bg-primary w-full sm:max-w-md sm:rounded-xl rounded-t-xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 fm-bg-primary p-4 border-b border-[var(--theme-border)] flex items-center justify-between">
              <h2 className="text-lg font-semibold fm-text-primary">Document Work</h2>
              <button onClick={() => setShowDocModal(false)} className="p-2">
                <X size={20} className="fm-text-secondary" />
              </button>
            </div>
            <form onSubmit={handleDocSubmit} className="p-4 pb-24 space-y-4">
              {/* Camera/Upload buttons */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  className="fm-btn fm-btn-secondary flex items-center justify-center gap-2 py-4"
                >
                  <Camera size={20} />
                  Take Photo
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="fm-btn fm-btn-secondary flex items-center justify-center gap-2 py-4"
                >
                  <Upload size={20} />
                  Upload
                </button>
              </div>
              
              {/* Hidden file inputs */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => handleImageCapture(e, 'camera')}
                className="hidden"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => handleImageCapture(e, 'file')}
                className="hidden"
              />

              {/* Image preview */}
              {docForm.imageData && (
                <div className="relative">
                  <img 
                    src={docForm.imageData} 
                    alt="Preview" 
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => setDocForm({ ...docForm, imageData: null })}
                    className="absolute top-2 right-2 p-1 bg-red-500 rounded-full"
                  >
                    <X size={16} className="text-white" />
                  </button>
                </div>
              )}

              <div>
                <label className="fm-label">Title *</label>
                <input
                  type="text"
                  value={docForm.title}
                  onChange={e => setDocForm({ ...docForm, title: e.target.value })}
                  className="fm-input"
                  placeholder="e.g., Before photo, Completed work"
                  required
                />
              </div>
              <div>
                <label className="fm-label">Related Work Order</label>
                <select
                  value={docForm.workOrderId}
                  onChange={e => setDocForm({ ...docForm, workOrderId: e.target.value })}
                  className="fm-select"
                >
                  <option value="">None</option>
                  {receivedDispatches.map(wo => (
                    <option key={wo.id} value={wo.id}>{wo.jobTitle}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="fm-label">Description</label>
                <textarea
                  value={docForm.description}
                  onChange={e => setDocForm({ ...docForm, description: e.target.value })}
                  className="fm-textarea"
                  rows="2"
                  placeholder="Optional notes about the photo"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowDocModal(false)}
                  className="flex-1 fm-btn fm-btn-secondary py-3"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={!docForm.imageData}
                  className={`flex-1 fm-btn fm-btn-primary py-3 ${!docForm.imageData ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Upload
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;

